import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { breakdownsApi, danceRolesApi, breakdownAnglesApi, breakdownClipTypesApi, movesApi, getAdminMediaUrl } from '../services/api';
import { MoveBreakdown, CreateMoveBreakdownData } from '../types';

const BreakdownsManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingBreakdown, setEditingBreakdown] = useState<MoveBreakdown | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filterMoveId, setFilterMoveId] = useState<number | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data: breakdownsData, isLoading } = useQuery({
    queryKey: ['breakdowns', { page, search, moveId: filterMoveId }],
    queryFn: () => breakdownsApi.getAll({ page, limit: 10, moveId: filterMoveId, search: search || undefined }),
  });

  // Reference data for dropdowns
  const { data: rolesData } = useQuery({
    queryKey: ['dance-roles-all'],
    queryFn: () => danceRolesApi.getAll({ limit: 100 }),
  });
  const { data: anglesData } = useQuery({
    queryKey: ['breakdown-angles-all'],
    queryFn: () => breakdownAnglesApi.getAll({ limit: 100 }),
  });
  const { data: clipTypesData } = useQuery({
    queryKey: ['breakdown-clip-types-all'],
    queryFn: () => breakdownClipTypesApi.getAll({ limit: 100 }),
  });
  const { data: movesData } = useQuery({
    queryKey: ['moves-all'],
    queryFn: () => movesApi.getAll({ limit: 200 }),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateMoveBreakdownData>();

  const createMutation = useMutation({
    mutationFn: breakdownsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breakdowns'] });
      reset();
      setShowForm(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateMoveBreakdownData> }) =>
      breakdownsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breakdowns'] });
      reset();
      setEditingBreakdown(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: breakdownsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breakdowns'] });
    },
  });

  const onSubmit = (data: CreateMoveBreakdownData) => {
    if (editingBreakdown) {
      updateMutation.mutate({ id: editingBreakdown.id, data });
    } else {
      const formData = new FormData();
      formData.append('moveId', String(data.moveId));
      formData.append('title', data.title);
      if (data.description) formData.append('description', data.description);
      formData.append('roleId', String(data.roleId));
      formData.append('angleId', String(data.angleId));
      formData.append('clipTypeId', String(data.clipTypeId));
      if (data.order !== undefined) formData.append('order', String(data.order));
      if (data.counts) formData.append('counts', data.counts);
      if (data.isPublished) formData.append('isPublished', 'true');

      const fileInput = fileInputRef.current;
      if (fileInput?.files?.[0]) {
        formData.append('video', fileInput.files[0]);
      } else if (data.videoUrl) {
        formData.append('videoUrl', data.videoUrl);
      }

      createMutation.mutate(formData);
    }
  };

  const handleEdit = (breakdown: MoveBreakdown) => {
    setEditingBreakdown(breakdown);
    reset({
      moveId: breakdown.moveId,
      title: breakdown.title,
      description: breakdown.description || '',
      roleId: breakdown.roleId,
      angleId: breakdown.angleId,
      clipTypeId: breakdown.clipTypeId,
      order: breakdown.order,
      counts: breakdown.counts || '',
      isPublished: breakdown.isPublished,
      videoUrl: breakdown.videoUrl,
    });
    setShowForm(true);
  };

  const handleDelete = (breakdown: MoveBreakdown) => {
    if (window.confirm(`Are you sure you want to delete "${breakdown.title}"?`)) {
      deleteMutation.mutate(breakdown.id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingBreakdown(null);
    reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const roles = rolesData?.data || [];
  const angles = anglesData?.data || [];
  const clipTypes = clipTypesData?.data || [];
  const moves = movesData?.data || [];

  return (
    <div className="breakdowns-management">
      <div className="page-header">
        <h1>Move Breakdowns Management</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)} disabled={showForm}>
          Add New Breakdown
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h2>{editingBreakdown ? 'Edit Breakdown' : 'Create New Breakdown'}</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="moveId">Move *</label>
                <select
                  {...register('moveId', { required: 'Move is required', valueAsNumber: true })}
                  id="moveId"
                  className={errors.moveId ? 'error' : ''}
                  disabled={!!editingBreakdown}
                >
                  <option value="">Select a move</option>
                  {moves.map((m) => (
                    <option key={m.id} value={m.id}>{m.name || m.title}</option>
                  ))}
                </select>
                {errors.moveId && <span className="error-text">{errors.moveId.message}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  {...register('title', { required: 'Title is required' })}
                  type="text"
                  id="title"
                  className={errors.title ? 'error' : ''}
                />
                {errors.title && <span className="error-text">{errors.title.message}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea {...register('description')} id="description" rows={2} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="roleId">Role *</label>
                <select
                  {...register('roleId', { required: 'Role is required', valueAsNumber: true })}
                  id="roleId"
                  className={errors.roleId ? 'error' : ''}
                >
                  <option value="">Select role</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
                {errors.roleId && <span className="error-text">{errors.roleId.message}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="angleId">Angle *</label>
                <select
                  {...register('angleId', { required: 'Angle is required', valueAsNumber: true })}
                  id="angleId"
                  className={errors.angleId ? 'error' : ''}
                >
                  <option value="">Select angle</option>
                  {angles.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
                {errors.angleId && <span className="error-text">{errors.angleId.message}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="clipTypeId">Clip Type *</label>
                <select
                  {...register('clipTypeId', { required: 'Clip type is required', valueAsNumber: true })}
                  id="clipTypeId"
                  className={errors.clipTypeId ? 'error' : ''}
                >
                  <option value="">Select clip type</option>
                  {clipTypes.map((ct) => (
                    <option key={ct.id} value={ct.id}>{ct.name}</option>
                  ))}
                </select>
                {errors.clipTypeId && <span className="error-text">{errors.clipTypeId.message}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="order">Order</label>
                <input {...register('order', { valueAsNumber: true })} type="number" id="order" defaultValue={0} />
              </div>
              <div className="form-group">
                <label htmlFor="counts">Counts</label>
                <input {...register('counts')} type="text" id="counts" placeholder="e.g. 1-2-3, 5-6-7" />
              </div>
            </div>

            {!editingBreakdown && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="video">Video File</label>
                  <input type="file" id="video" ref={fileInputRef} accept="video/*" />
                </div>
                <div className="form-group">
                  <label htmlFor="videoUrl">Or Video URL</label>
                  <input {...register('videoUrl')} type="text" id="videoUrl" placeholder="https://..." />
                </div>
              </div>
            )}

            <div className="form-group">
              <label>
                <input {...register('isPublished')} type="checkbox" />
                {' '}Published
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={createMutation.isPending || updateMutation.isPending}>
                {editingBreakdown ? 'Update' : 'Create'} Breakdown
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search breakdowns..."
          aria-label="Search breakdowns"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select
          value={filterMoveId || ''}
          onChange={(e) => setFilterMoveId(e.target.value ? Number(e.target.value) : undefined)}
          className="filter-select"
          aria-label="Filter by move"
        >
          <option value="">All Moves</option>
          {moves.map((m) => (
            <option key={m.id} value={m.id}>{m.name || m.title}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="loading">Loading breakdowns...</div>
      ) : breakdownsData && breakdownsData.data.length > 0 ? (
        <>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Move</th>
                  <th>Role</th>
                  <th>Angle</th>
                  <th>Clip Type</th>
                  <th>Order</th>
                  <th>Published</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {breakdownsData.data.map((breakdown) => (
                  <tr key={breakdown.id}>
                    <td>
                      <strong>{breakdown.title}</strong>
                      {breakdown.thumbnail && (
                        <img
                          src={getAdminMediaUrl(breakdown.thumbnail)}
                          alt=""
                          className="breakdown-thumb"
                        />
                      )}
                    </td>
                    <td>{breakdown.move?.title || breakdown.moveId}</td>
                    <td>{breakdown.role?.name || '-'}</td>
                    <td>{breakdown.angle?.name || '-'}</td>
                    <td>{breakdown.clipType?.name || '-'}</td>
                    <td>{breakdown.order}</td>
                    <td>
                      <span className={`status-badge ${breakdown.isPublished ? 'status-active' : 'status-inactive'}`}>
                        {breakdown.isPublished ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-small btn-secondary" onClick={() => handleEdit(breakdown)}>Edit</button>
                        <button className="btn btn-small btn-danger" onClick={() => handleDelete(breakdown)} disabled={deleteMutation.isPending}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {breakdownsData.pagination.pages > 1 && (
            <div className="pagination">
              {Array.from({ length: breakdownsData.pagination.pages }, (_, i) => i + 1).map((pageNum) => (
                <button key={pageNum} className={`btn btn-small ${pageNum === page ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setPage(pageNum)}>
                  {pageNum}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <p>No breakdowns found. Create your first move breakdown!</p>
        </div>
      )}
    </div>
  );
};

export default BreakdownsManagement;
